import { useState, useRef, useEffect } from "react";

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  sendLabel?: string;
}

export default function ChatInput({
  onSend,
  disabled = false,
  placeholder = "Type your response...",
  sendLabel = "Send",
}: ChatInputProps) {
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
    }
  }, [text]);

  function handleSubmit() {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText("");
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  return (
    <div className="flex-shrink-0 border-t border-slate-800 bg-slate-900 px-3 sm:px-4 py-3">
      <div className="max-w-3xl mx-auto flex items-end gap-2">
        <textarea
          data-testid="chat-input"
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          rows={1}
          className="flex-1 bg-slate-800 text-slate-100 rounded-xl px-4 py-2.5 text-sm sm:text-base resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <button
          data-testid="chat-send-btn"
          onClick={handleSubmit}
          disabled={disabled || !text.trim()}
          className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-xl px-4 py-2.5 text-sm font-medium transition-colors flex-shrink-0"
        >
          {sendLabel}
        </button>
      </div>
    </div>
  );
}
